import { reactive, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import LaisQualificationQuestionsAPI from 'dashboard/api/laisQualificationQuestions';
import {
  qualificationQuestionNeedsOptions,
  splitQualificationOptions,
} from '../utils/minhaLaisPresenters';

/**
 * Essential/qualifying questions the assistant asks a lead.
 *
 * `canUpdateSettings` is the admin gate resolved by the page and reused here so
 * the create/update/delete guards stay identical to the server policy.
 */
export function useMinhaLaisQualificationQuestions({ canUpdateSettings }) {
  const isLoadingQualificationQuestions = ref(false);
  const savingQualificationQuestionId = ref(null);
  const deletingQualificationQuestionId = ref(null);
  const qualificationQuestions = ref([]);
  const qualificationQuestionLimits = ref({
    essential: 8,
    qualifying: 5,
    active_essential: 0,
    active_qualifying: 0,
  });
  const newQualificationQuestion = reactive({
    key: '',
    label: '',
    prompt_hint: '',
    operation_scope: 'both',
    answer_type: 'text',
    options_text: '',
    essential: true,
    qualifying: false,
    active: true,
    position: 1,
  });

  const resetNewQualificationQuestion = () => {
    Object.assign(newQualificationQuestion, {
      key: '',
      label: '',
      prompt_hint: '',
      operation_scope: 'both',
      answer_type: 'text',
      options_text: '',
      essential: true,
      qualifying: false,
      active: true,
      position: qualificationQuestions.value.length + 1,
    });
  };

  const applyQualificationQuestionsPayload = payload => {
    qualificationQuestions.value = (payload.data || []).map(question => ({
      ...question,
      options_text: (question.options || []).join('\n'),
    }));
    qualificationQuestionLimits.value = {
      ...qualificationQuestionLimits.value,
      ...(payload.limits || {}),
    };
    resetNewQualificationQuestion();
  };

  const carregarQualificationQuestions = async () => {
    isLoadingQualificationQuestions.value = true;
    try {
      const { data } = await LaisQualificationQuestionsAPI.list();
      applyQualificationQuestionsPayload(data);
    } catch (error) {
      useAlert('Não foi possível carregar as perguntas de qualificação.');
    } finally {
      isLoadingQualificationQuestions.value = false;
    }
  };

  const qualificationQuestionPayload = question => ({
    key: question.key,
    label: question.label,
    prompt_hint: question.prompt_hint,
    operation_scope: question.operation_scope,
    answer_type: question.answer_type,
    options: qualificationQuestionNeedsOptions(question)
      ? splitQualificationOptions(question.options_text || '')
      : [],
    essential: Boolean(question.essential),
    qualifying: Boolean(question.qualifying),
    active: Boolean(question.active),
    position:
      Number(question.position) || qualificationQuestions.value.length + 1,
  });

  const criarQualificationQuestion = async () => {
    if (!canUpdateSettings.value) {
      useAlert('Apenas administradores podem criar perguntas.');
      return;
    }
    if (!newQualificationQuestion.label.trim()) {
      useAlert('Informe o texto da pergunta.');
      return;
    }

    savingQualificationQuestionId.value = 'new';
    try {
      const { data } = await LaisQualificationQuestionsAPI.create(
        qualificationQuestionPayload(newQualificationQuestion)
      );
      applyQualificationQuestionsPayload(data);
      useAlert('Pergunta criada.');
    } catch (error) {
      useAlert(
        error.response?.data?.errors?.[0] ||
          'Não foi possível criar a pergunta.'
      );
    } finally {
      savingQualificationQuestionId.value = null;
    }
  };

  const salvarQualificationQuestion = async question => {
    if (!canUpdateSettings.value) {
      useAlert('Apenas administradores podem editar perguntas.');
      return;
    }

    savingQualificationQuestionId.value = question.id;
    try {
      const { data } = await LaisQualificationQuestionsAPI.update(
        question.id,
        qualificationQuestionPayload(question)
      );
      applyQualificationQuestionsPayload(data);
      useAlert('Pergunta salva.');
    } catch (error) {
      useAlert(
        error.response?.data?.errors?.[0] ||
          'Não foi possível salvar a pergunta.'
      );
    } finally {
      savingQualificationQuestionId.value = null;
    }
  };

  const removerQualificationQuestion = async question => {
    if (!canUpdateSettings.value) return;

    deletingQualificationQuestionId.value = question.id;
    try {
      const { data } = await LaisQualificationQuestionsAPI.delete(question.id);
      applyQualificationQuestionsPayload(data);
      useAlert('Pergunta removida.');
    } catch (error) {
      useAlert(
        error.response?.data?.errors?.[0] ||
          'Não foi possível remover a pergunta.'
      );
    } finally {
      deletingQualificationQuestionId.value = null;
    }
  };

  return {
    qualificationQuestions,
    qualificationQuestionLimits,
    newQualificationQuestion,
    isLoadingQualificationQuestions,
    savingQualificationQuestionId,
    deletingQualificationQuestionId,
    carregarQualificationQuestions,
    criarQualificationQuestion,
    salvarQualificationQuestion,
    removerQualificationQuestion,
  };
}
